import { isPlottableCoord } from './geocode';

/**
 * A map image for devices that cannot run the interactive map.
 *
 * Mapbox GL v3 requires WebGL 2. Some real browsers don't have it — hardware
 * acceleration switched off, older Safari, VMs and remote desktops, privacy
 * extensions that block the context. Those buyers were shown a notice and no map
 * at all, which is a worse outcome than a picture: a still map with the homes
 * pinned answers "where is this?", which is most of what the map is for.
 *
 * The Static Images API is plain HTTP — no WebGL, no Mapbox bundle. It uses the
 * same public token and style as the live map, so the fallback looks like the
 * product rather than a apology.
 */

const STYLE = 'mapbox/light-v11';
/**
 * The accent, matching the interactive pins (`bg-emerald-500` in SearchMap).
 * Mapbox wants hex without the '#'.
 *
 * A LITERAL, AND THAT IS WHY IT DRIFTED. This said `10b981` — the green the
 * accent used to be — long after the 2026-08-13 crimson re-theme redefined
 * `--color-emerald-500` to #a41f2e. Every Tailwind class in the app followed the
 * token; this string could not, because it is interpolated into a Mapbox URL
 * rather than resolved by CSS. So the still-map fallback pinned homes in green
 * while the live map pinned them in crimson, and the comment above it claimed
 * they matched.
 *
 * Kept in step by `staticMap.test.ts`, which reads the token out of globals.css
 * rather than trusting this line.
 */
export const PIN_COLOUR = 'a41f2e';
/**
 * Overlay cap. Mapbox rejects very long URLs, and beyond a couple of dozen pins
 * a still image is unreadable anyway — the list is the better answer at that
 * point. We pin the first N and say so rather than silently truncating.
 */
export const STATIC_MAP_PIN_LIMIT = 20;

export interface StaticMapPoint {
  latitude: number;
  longitude: number;
}

function token(): string | undefined {
  return process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
}

/**
 * A still map with one pin, centred on it. `null` when there's no token or the
 * coordinate isn't honest — callers render nothing rather than a map of the
 * Atlantic (see isPlottableCoord).
 */
export function staticMapForPoint(
  { latitude, longitude }: StaticMapPoint,
  { width = 640, height = 360, zoom = 14 } = {},
): string | null {
  const t = token();
  if (!t || !isPlottableCoord(latitude, longitude)) return null;
  const pin = `pin-l+${PIN_COLOUR}(${longitude},${latitude})`;
  return (
    `https://api.mapbox.com/styles/v1/${STYLE}/static/${pin}` +
    `/${longitude},${latitude},${zoom}/${width}x${height}@2x?access_token=${t}`
  );
}

/**
 * A still map with every plottable home pinned, auto-framed to fit them.
 * `null` when nothing is plottable — the caller then has nothing honest to draw.
 */
export function staticMapForPoints(
  points: StaticMapPoint[],
  { width = 640, height = 640, limit = STATIC_MAP_PIN_LIMIT } = {},
): string | null {
  const t = token();
  if (!t) return null;
  const plottable = points.filter((p) => isPlottableCoord(p.latitude, p.longitude)).slice(0, limit);
  if (plottable.length === 0) return null;
  // One pin: 'auto' would zoom to the whole world, so centre on it instead.
  if (plottable.length === 1) {
    return staticMapForPoint(plottable[0], { width, height, zoom: 13 });
  }
  const pins = plottable
    .map((p) => `pin-s+${PIN_COLOUR}(${p.longitude},${p.latitude})`)
    .join(',');
  // 'auto' frames the overlays — the still-image equivalent of fitBounds.
  return (
    `https://api.mapbox.com/styles/v1/${STYLE}/static/${pins}` +
    `/auto/${width}x${height}@2x?access_token=${t}`
  );
}
